import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { MenuItem, PROFILE_MENU_ITEMS } from '@constants/navi';
import { checkIsActive, KTSVG } from '@helpers/index';
import { getCustomerSelector } from '@modules/customer';
import { useClickOutside } from '@shared/index';
import { ProfileLanguageOptions } from './ProfileLanguageOptions';

/**
 * The language picker is slotted in above this entry rather than appended after the
 * whole menu, because logout is a terminal action and belongs at the bottom.
 *
 * Matched by id rather than by position so that reordering the constant doesn't
 * silently move the picker. If the id ever changes the picker lands after logout —
 * cosmetically wrong, but nothing breaks.
 */
const TRAILING_ITEM_ID = 'logout';

export const UserProfileDropdown = () => {
  const [isOpened, setIsOpened] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const intl = useIntl();
  const { pathname } = useLocation();
  const customer = useSelector(getCustomerSelector);

  const handleDropdownToggle = () => setIsOpened((prev) => !prev);

  useClickOutside(ref, () => {
    setIsOpened(false);
  });

  const renderItem = (item: MenuItem) => {
    const isActive = checkIsActive(pathname, item.to);

    if (item.type === 'external') {
      return (
        <a
          href={item.to}
          target="_blank"
          rel="noopener noreferrer"
          key={item.id}
          className={clsx('profile-dropdown-menu-content-item', {
            'profile-dropdown-menu-content-item-active': isActive,
          })}>
          <KTSVG path={item.icon} className="svg-icon-2" />
          <span className="profile-dropdown-menu-content-text">
            {intl.formatMessage({ id: item.title })}
          </span>
        </a>
      );
    }

    return (
      <Link
        to={item.to}
        key={item.id}
        className={clsx('profile-dropdown-menu-content-item', {
          'profile-dropdown-menu-content-item-active': isActive,
        })}>
        <KTSVG path={item.icon} className="svg-icon-2" />
        <span className="profile-dropdown-menu-content-text">
          {intl.formatMessage({ id: item.title })}
        </span>
      </Link>
    );
  };

  return (
    <div
      ref={ref}
      className="relative cursor-pointer profile-dropdown"
      onClick={handleDropdownToggle}>
      <div
        className={clsx('profile-dropdown-button', {
          'profile-dropdown-opened': isOpened,
        })}>
        <span className="profile-dropdown-button-label">{customer?.firstName}</span>
        <div className="profile-dropdown-button-avatar">
          <KTSVG path={'/media/svg/general/login_icon.svg'} className="svg-icon-2" />
        </div>
      </div>
      <div className={clsx('profile-dropdown-menu', { block: isOpened, hidden: !isOpened })}>
        <div className="profile-dropdown-menu-content">
          {PROFILE_MENU_ITEMS.filter((item) => item.id !== TRAILING_ITEM_ID).map(renderItem)}

          <ProfileLanguageOptions />

          {PROFILE_MENU_ITEMS.filter((item) => item.id === TRAILING_ITEM_ID).map(renderItem)}
        </div>
      </div>
    </div>
  );
};
